/**
 * DraftSeatPropertiesPanel - Metadata editor for a selected local seat draft.
 * Elvis Aloha Collection - Map Editor
 *
 * Lets the owner label a drawn box AFTER drawing (draw-first workflow):
 * Chart, Section, Row, Seat, Rotation, Native X/Y, Width, Height, Template,
 * Size lock. Alphanumeric Row values (01, A, QA, BOX-1) are preserved as
 * strings and never coerced to numbers. All edits stay local until Save.
 */

import React from 'react';

const labelStyle = { display: 'block', fontSize: 11, fontWeight: 600, color: '#666', marginBottom: 3 };
const inputStyle = { width: '100%', padding: '7px 9px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const rowStyle = { display: 'flex', gap: 8, marginBottom: 10 };
const dupBtn = { padding: '7px 0', background: 'white', color: '#2563EB', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, cursor: 'pointer' };

const DraftSeatPropertiesPanel = ({
  draft,
  chartLabel,
  templateName,
  onChange,
  onSave,
  onDuplicate,
  onDuplicateDirection,
  onSaveAsTemplate,
  onDelete,
  onClose,
  saving,
}) => {
  if (!draft) return null;

  const set = (field) => (e) => {
    const raw = e.target.value;
    onChange(draft.id, { [field]: raw });
  };
  const setNum = (field) => (e) => {
    const v = parseFloat(e.target.value);
    onChange(draft.id, { [field]: Number.isFinite(v) ? v : 0 });
  };

  return (
    <div
      style={{
        position: 'absolute', top: 80, right: 20, background: 'white', borderRadius: 12,
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)', zIndex: 500, width: 280,
        maxHeight: 'calc(100vh - 160px)', display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}
      data-testid="draft-seat-properties-panel"
    >
      <div style={{ padding: '14px 18px 10px', borderBottom: '1px solid #eee', flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Seat Draft</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer' }} data-testid="close-draft-panel">×</button>
        </div>
        <div style={{ marginTop: 6 }}>
          <span style={{ display: 'inline-block', padding: '3px 9px', background: draft.error ? '#FEE2E2' : '#DCFCE7', color: draft.error ? '#B91C1C' : '#166534', borderRadius: 12, fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }} data-testid="draft-status-badge">
            {draft.error ? 'Invalid' : 'Unsaved Draft'}
          </span>
        </div>
        {draft.error && (
          <div style={{ marginTop: 8, fontSize: 11, color: '#B91C1C', background: '#FEF2F2', padding: '6px 8px', borderRadius: 6 }} data-testid="draft-error-message">
            {draft.error}
          </div>
        )}
      </div>

      <div style={{ padding: '14px 18px', overflowY: 'auto', overscrollBehavior: 'contain', flex: 1 }}>
        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>Chart</label>
          <div style={{ ...inputStyle, background: '#f5f5f5' }}>{chartLabel}</div>
        </div>

        <div style={rowStyle}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Section</label>
            <input type="text" value={draft.section} onChange={(e) => onChange(draft.id, { section: e.target.value.toUpperCase() })} style={inputStyle} data-testid="draft-section-input" />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Row</label>
            <input type="text" value={draft.row} onChange={set('row')} placeholder="e.g. 01, QA" style={inputStyle} data-testid="draft-row-input" />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Seat</label>
            <input type="text" value={draft.seat} onChange={set('seat')} style={inputStyle} data-testid="draft-seat-input" />
          </div>
        </div>

        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>Template</label>
          <div style={{ ...inputStyle, background: '#f5f5f5', fontSize: 12 }}>{templateName || '—'}</div>
        </div>

        <div style={rowStyle}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Native X</label>
            <input type="number" value={Math.round(draft.native_x)} onChange={setNum('native_x')} style={inputStyle} data-testid="draft-x-input" />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Native Y</label>
            <input type="number" value={Math.round(draft.native_y)} onChange={setNum('native_y')} style={inputStyle} data-testid="draft-y-input" />
          </div>
        </div>

        <div style={rowStyle}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Width</label>
            <input type="number" value={Math.round(draft.width)} disabled={draft.size_locked} onChange={setNum('width')} style={{ ...inputStyle, background: draft.size_locked ? '#f5f5f5' : 'white' }} data-testid="draft-width-input" />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Height</label>
            <input type="number" value={Math.round(draft.height)} disabled={draft.size_locked} onChange={setNum('height')} style={{ ...inputStyle, background: draft.size_locked ? '#f5f5f5' : 'white' }} data-testid="draft-height-input" />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Rotation°</label>
            <input type="number" value={Math.round(draft.rotation)} onChange={setNum('rotation')} style={inputStyle} data-testid="draft-rotation-input" />
          </div>
        </div>

        <div style={{ marginBottom: 10 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={draft.size_locked} onChange={(e) => onChange(draft.id, { size_locked: e.target.checked })} data-testid="draft-size-lock" />
            <span style={{ fontSize: 13 }}>Lock size</span>
          </label>
        </div>
      </div>

      <div style={{ padding: '12px 18px 16px', borderTop: '1px solid #eee', flexShrink: 0 }}>
        <button onClick={() => onSave(draft.id)} disabled={saving} style={{ width: '100%', padding: 11, background: saving ? '#ccc' : '#16A34A', color: 'white', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: saving ? 'default' : 'pointer', marginBottom: 8 }} data-testid="save-draft-btn">
          {saving ? 'Saving…' : 'Save This Seat'}
        </button>

        <button onClick={() => onSaveAsTemplate(draft.id)} style={{ width: '100%', padding: 9, background: 'white', color: '#7C3AED', border: '1px solid #7C3AED', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', marginBottom: 8 }} data-testid="save-as-template-btn">
          Save as Template
        </button>

        {/* Directional duplicates */}
        <div style={{ fontSize: 10, color: '#888', marginBottom: 4, fontWeight: 700, letterSpacing: '0.05em' }}>DUPLICATE</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginBottom: 8 }}>
          <button onClick={() => onDuplicateDirection(draft.id, 'left')} style={dupBtn} data-testid="dup-left-btn" title="Duplicate Left">◀</button>
          <button onClick={() => onDuplicateDirection(draft.id, 'right')} style={dupBtn} data-testid="dup-right-btn" title="Duplicate Right">▶</button>
          <button onClick={() => onDuplicateDirection(draft.id, 'up')} style={dupBtn} data-testid="dup-up-btn" title="Duplicate Up">▲</button>
          <button onClick={() => onDuplicateDirection(draft.id, 'down')} style={dupBtn} data-testid="dup-down-btn" title="Duplicate Down">▼</button>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => onDuplicate(draft.id)} style={{ flex: 1, padding: 9, background: 'white', color: '#2563EB', border: '1px solid #2563EB', borderRadius: 6, fontSize: 12, cursor: 'pointer' }} data-testid="duplicate-draft-btn">
            Duplicate
          </button>
          <button onClick={() => onDelete(draft.id)} style={{ flex: 1, padding: 9, background: 'white', color: '#d44', border: '1px solid #d44', borderRadius: 6, fontSize: 12, cursor: 'pointer' }} data-testid="delete-draft-btn">
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

export default DraftSeatPropertiesPanel;
